import type { AxiosInstance, AxiosResponse } from 'axios'
import type { email_aziendale, ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { DocumentPermissions } from '@/enums'

export type TicketUpsertRequestDto = {
  siteId: number | null
  statusId: number
  ticketTypeId: number
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  duration: number
  urgent: boolean
  waitingForCustomer: boolean
  platformId: number | null
  applicationId: number | null
  problemTypeId: number | null
  problemTopicId: number | null
  description: string | null
  solution: string | null
  resolving: boolean
  customerId: string | null
  additionalEmails: string[]
  dumpIds: number[]
}

export type TicketUpsertResponseDto = {
  ticketRecordId: number
  ticketId: string
  openedAtUtc?: string | null
  siteId?: number | null
  statusId: number
  statusName?: string | null
  ticketTypeId?: number | null
  ticketTypeName?: string | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  duration: number
  urgent: boolean
  waitingForCustomer: boolean
  platformId?: number | null
  applicationId?: number | null
  problemTypeId?: number | null
  problemTopicId?: number | null
  description?: string | null
  solution?: string | null
  customerId?: string | null
  primaryCorrespondentDisplayName?: string | null
  primaryCorrespondentEmail?: string | null
  additionalEmails: string[]
  dumpIds: number[]
}

export type TicketDuplicateResponseDto = {
  ticketRecordId: number
  ticketId: string
}

export type TicketAttachmentPermissionsRequestDto = {
  objectId?: number
  path: string
}

export type TicketAttachmentFolderRequestDto = {
  objectId?: number
  rootFolder: string
  newFolderName: string
  conflictBehavior?: string
}

const ticketAttachmentPermissionsCache = new Map<
  string,
  {
    value: DocumentPermissions
    expiresAt: number
  }
>()
const ticketAttachmentPermissionsPendingRequests = new Map<string, Promise<DocumentPermissions>>()
const TICKET_ATTACHMENT_PERMISSIONS_CACHE_TTL_MS = 10 * 60 * 1000

const getTicketRouteId = (ticketRecordId: number | null | undefined) => {
  return typeof ticketRecordId === 'number' && Number.isFinite(ticketRecordId) ? String(ticketRecordId) : null
}

const getTicketAttachmentPermissionsCacheKey = (
  ticketRecordId: number | null | undefined,
  request: TicketAttachmentPermissionsRequestDto,
) => {
  return `${getTicketRouteId(ticketRecordId) ?? ''}|${request.objectId ?? ''}|${request.path}`
}

const getDistinctStrings = (values: Array<string | null | undefined>) => {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value))]
}

const getDistinctNumbers = (values: Array<number | null | undefined>) => {
  return [...new Set(values.filter((value): value is number => typeof value === 'number' && value > 0))]
}

const mapAdditionalEmails = (emails: email_aziendale[] | null | undefined) =>
  getDistinctStrings(emails?.map((email) => email.email) ?? [])

const getOptionalLookupId = <T extends { id?: number | null }>(value: T | null | undefined) => {
  return typeof value?.id === 'number' && value.id >= 0 ? value.id : null
}

export const mapTicketToUpsertRequest = (
  currentTicket: ticket,
  options: {
    resolving?: boolean
  } = {},
): TicketUpsertRequestDto => ({
  siteId: getOptionalLookupId(currentTicket.sede),
  statusId: currentTicket.stato?.id ?? 0,
  ticketTypeId: currentTicket.tipologia_ticket?.id ?? 0,
  contactName: currentTicket.leg_contatto ?? null,
  contactEmail: currentTicket.leg_email_contatto ?? null,
  contactPhone: currentTicket.leg_phone_contatto ?? null,
  duration: currentTicket.durata ?? 0,
  urgent: !!currentTicket.urgente,
  waitingForCustomer: !!currentTicket.attesa,
  platformId: getOptionalLookupId(currentTicket.piattaforma),
  applicationId: getOptionalLookupId(currentTicket.applicazione),
  problemTypeId: getOptionalLookupId(currentTicket.tipologia_problema),
  problemTopicId: getOptionalLookupId(currentTicket.argomento_problema),
  description: currentTicket.descrizione ?? null,
  solution: currentTicket.soluzione ?? null,
  resolving: !!options.resolving,
  customerId: currentTicket.customer_id ?? null,
  additionalEmails: mapAdditionalEmails(currentTicket.email_addizionali),
  dumpIds: getDistinctNumbers(currentTicket.dumps?.map((dump) => dump.id) ?? []),
})

export const createTicketWithApi = (client: AxiosInstance, currentTicket: ticket) => {
  return client.post<TicketUpsertResponseDto, AxiosResponse<TicketUpsertResponseDto>>(
    '/api/tickets',
    mapTicketToUpsertRequest(currentTicket),
  )
}

export const updateTicketWithApi = (
  client: AxiosInstance,
  currentTicket: ticket,
  options: {
    resolving?: boolean
  } = {},
) => {
  return client.put<TicketUpsertResponseDto, AxiosResponse<TicketUpsertResponseDto>>(
    `/api/tickets/${currentTicket.id}`,
    mapTicketToUpsertRequest(currentTicket, options),
  )
}

export const duplicateTicketWithApi = (client: AxiosInstance, ticketRecordId: number) => {
  return client.post<TicketDuplicateResponseDto, AxiosResponse<TicketDuplicateResponseDto>>(
    `/api/tickets/${ticketRecordId}/duplicate`,
  )
}

export const getTicketAttachmentPermissions = async (
  client: AxiosInstance,
  ticketRecordId: number | null | undefined,
  request: TicketAttachmentPermissionsRequestDto,
) => {
  const routeTicketId = getTicketRouteId(ticketRecordId)
  // Backend requires both a ticket id and a non-empty path matching the ticket's
  // attachmentsFolderUrl; skip the call otherwise (it would throw a
  // ForbiddenAccessException server-side).
  if (!routeTicketId || !request.path?.trim()) {
    return DocumentPermissions.Deny
  }

  const cacheKey = getTicketAttachmentPermissionsCacheKey(ticketRecordId, request)
  const cachedValue = ticketAttachmentPermissionsCache.get(cacheKey)
  if (cachedValue && cachedValue.expiresAt > Date.now()) {
    return cachedValue.value
  }

  const pendingRequest = ticketAttachmentPermissionsPendingRequests.get(cacheKey)
  if (pendingRequest) {
    return await pendingRequest
  }

  try {
    const requestPromise = client
      .post<DocumentPermissions, AxiosResponse<DocumentPermissions>>(
        `/api/tickets/${routeTicketId}/attachments/permissions`,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then((response) => (response.status === 200 ? response.data : DocumentPermissions.Deny))
      .finally(() => {
        ticketAttachmentPermissionsPendingRequests.delete(cacheKey)
      })

    ticketAttachmentPermissionsPendingRequests.set(cacheKey, requestPromise)

    const permission = await requestPromise
    ticketAttachmentPermissionsCache.set(cacheKey, {
      value: permission,
      expiresAt: Date.now() + TICKET_ATTACHMENT_PERMISSIONS_CACHE_TTL_MS,
    })
    return permission
  } catch (error) {
    console.log(`Errore in settaggio permessi sulla cartella ${request.path}`, error)
    ticketAttachmentPermissionsPendingRequests.delete(cacheKey)
    return DocumentPermissions.Deny
  }
}

export const createTicketAttachmentFolder = (
  client: AxiosInstance,
  ticketRecordId: number,
  request: TicketAttachmentFolderRequestDto,
) => {
  return client.post(`/api/tickets/${ticketRecordId}/attachments/folders`, request, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
}
